import { State, Action, StateContext, Selector, Store, Actions, ofActionDispatched } from '@ngxs/store';
import { BookmarksStateModel } from './bookmarks.state.model';
import {
  LoadBookmarks,
  LoadBookmarksSuccess,
  LoadOrganizationBookmarks,
  DeleteBookmark,
  DeleteBookmarkSuccess,
  SaveBookmark,
  SaveBookmarkSuccess,
  SelectBookmark,
  SelectBookmarkSuccess,
  SaveBookmarkFail,
  SaveBookmarkThumbnails,
  ResetBookmarks,
  SortBookmarks,
  CheckBookmark,
  SetCheckedBookmarks,
  ExportBookmarks,
  ExportBookmarksSuccess,
  ExportBookmarksError,
  LoadCollectionBookmarks,
  DeleteBookmarkFail,
  DeleteFavorite,
  DeleteFavoriteSuccess,
  DeleteFavoriteFail,
  CopyBookmark,
  CopyBookmarkFailure,
  CopyBookmarkSuccess,
  SetBookmarkText,
  SetSelectedCategory,
  ClearBookmarks,
  UpdateBookmarkCollection,
  UpdateBookmarkCollectionSuccess,
  UpdateBookmarkCollectionError,
  RemoveBookmarkCollectionSuccess,
  AddBookmarkCollectionSuccess,
  RemoveBookmarkCollectionError,
  AddBookmarkCollectionError,
  CancelCopyBookmark,
  CancelSaveBookmark
} from './bookmarks.actions';

import { finalize, flatMap, filter, tap, takeUntil, take } from 'rxjs/operators';
import { BookmarksService } from '../bookmarks.service';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { concat, forkJoin } from 'rxjs';
import { RegisterChapterThumbnails } from 'src/app/story/state/chapters/story.chapters.actions';
import { bookmarkSortRecent } from '../data/bookmark-sort';
import { PublishService } from 'src/app/publish/publish.service';
import { WidgetOptions } from 'src/app/publish/models/widget-options.model';
import { OrganizationsService } from 'src/app/organizations/organizations.service';
import { CollectionContentModel } from 'src/app/organizations/models/collection.content.model';
import { ExploreService } from 'src/app/explore/explore.service';
import { StoryModel } from 'src/app/story/models/story.model';
import { get, invert, mapKeys } from 'lodash';
import { sortModels } from '../models/bookmark-sort.model';
import { ChapterUtils } from 'src/app/shared/chapter-utils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ParticleActions } from 'src/app/engine/state/particles/particles.actions';
import { DialogActions } from 'src/app/dialogs/state/dialog.actions';
import { Injectable } from '@angular/core';
import { BookmarkEntryModel } from '../models/bookmark.entry.model';
import { StoryService } from 'src/app/story/story.service';
import { ContentType } from 'src/app/story/models/content-type.enum';
import { BookmarkModel } from '../models/bookmark.model';

const BookmarksStateDefaults = {
  loading: true,
  exporting: false,
  copying: false,
  saving: false,
  bookmarks: [],
  selectedBookmark: null,
  sort: bookmarkSortRecent,
  checkedBookmarks: [],
  selectedCategory: null
};

@State<BookmarksStateModel>({
  name: 'bookmarks',
  defaults: BookmarksStateDefaults
})
@Injectable()
export class BookmarksState {
  @Selector()
  static getBookmarks(state: BookmarksStateModel) {
    const { bookmarks, sort } = state;
    const { ascending, fields } = sort;
    return sortModels(bookmarks, fields, ascending);
  }

  @Selector()
  static getCheckedBookmarks(state: BookmarksStateModel) {
    return state.checkedBookmarks;
  }

  @Selector()
  static getSelectedBookmark(state: BookmarksStateModel) {
    return state.selectedBookmark;
  }

  @Selector()
  static getLoading(state: BookmarksStateModel) {
    return state.loading;
  }

  @Selector()
  static getExporting(state: BookmarksStateModel) {
    return state.exporting;
  }

  @Selector()
  static getSaving(state: BookmarksStateModel) {
    return state.saving;
  }

  @Selector()
  static getCopying(state: BookmarksStateModel) {
    return state.copying;
  }

  @Selector()
  static getSort(state: BookmarksStateModel) {
    return state.sort;
  }

  @Selector()
  static getSelectedCategory(state: BookmarksStateModel) {
    return state.selectedCategory;
  }

  @Action(LoadBookmarks)
  loadBookmarks(context: StateContext<BookmarksStateModel>, action: LoadBookmarks) {
    context.patchState({
      loading: true,
      bookmarks: [],
      checkedBookmarks: []
    });

    // load private content (private bookmarks, custom modules, and favorites)
    forkJoin(
      this.bookmarksService.getPrivateBookmarks(action.query),
      this.bookmarksService.getPrivateConditions(action.query),
      this.bookmarksService.getFavorites()
    )
      .pipe(
        finalize(() => { context.patchState({ loading: false }); })
      )
      .subscribe(([bookmarks, conditions, favorites]) => {
        context.dispatch(new LoadBookmarksSuccess([
          ...bookmarks,
          ...conditions,
          ...favorites
        ]));
      });
  }


  @Action(LoadOrganizationBookmarks)
  loadOrganizationBookmarks(context: StateContext<BookmarksStateModel>, { organizationId }: LoadOrganizationBookmarks) {
    context.patchState({
      loading: true,
      bookmarks: [],
      checkedBookmarks: []
    });

    // load group content (private bookmarks and custom modules)
    this.bookmarksService.getOrganizationContent(organizationId)
      .pipe(
        finalize(() => { context.patchState({ loading: false }); })
      )
      .subscribe((bookmarks) => {
        context.dispatch(new LoadBookmarksSuccess(bookmarks));
      });
  }

  @Action(LoadCollectionBookmarks)
  loadCollectionBookmarks(context: StateContext<BookmarksStateModel>, action: LoadCollectionBookmarks) {
    context.patchState({
      loading: true,
      bookmarks: [],
      checkedBookmarks: []
    });

    this.organizationsService.getCollectionContent(action.organizationId, action.collectionId)
      .pipe(
        finalize(() => { context.patchState({ loading: false }); })
      )
      .subscribe((result) => {
        context.dispatch(new LoadBookmarksSuccess(result));
      });
  }

  @Action(LoadBookmarksSuccess)
  loadSuccess({ patchState }: StateContext<BookmarksStateModel>, action: LoadBookmarksSuccess) {
    patchState({
      bookmarks: action.result
    });
  }

  @Action(SelectBookmark)
  loadBookmark(context: StateContext<BookmarksStateModel>, action: SelectBookmark) {
    const { id, type } = action;

    const bookmark$ = type === 'bookmark'
      ? this.bookmarksService.getBookmarkModel(id)
      : this.exploreService.getConditionModel(id);

    bookmark$
      .subscribe((bookmark) => {
        context.dispatch(new SelectBookmarkSuccess(bookmark));
      });
  }

  @Action(SelectBookmarkSuccess)
  selectBookmarkSuccess(context: StateContext<BookmarksStateModel>, action: SelectBookmarkSuccess) {
    context.patchState({
      selectedBookmark: action.bookmark
    });
  }

  // Replace existing or Save new bookmark
  @Action(SaveBookmark)
  async saveBookmark(context: StateContext<BookmarksStateModel>, action: SaveBookmark) {
    const bookmarkEntry = action.bookmarkEntry;

    // begin save
    context.patchState({ saving: true });

    // requires story
    let serialized: { story: StoryModel };

    const chapters = this.store.selectSnapshot(StoryChaptersState.getStoryChapters);
    const makeNewChapterIds = action.inplace === false;
    const chapterIdMap = ChapterUtils.makeChapterIdMap(chapters, makeNewChapterIds);

    if (makeNewChapterIds) {
      await this.store.dispatch(new ParticleActions.UpdateTweenParticleLookupChapters(chapterIdMap)).toPromise();
    }

    if (action.includeIndex) {
      serialized = this.bookmarksService.createBookmarkStory(chapterIdMap); // Just a serialized module
      bookmarkEntry.index = serialized.story;
    }

    const method = bookmarkEntry.id ? 'update' : 'save';

    this.bookmarksService.saveBookmark(bookmarkEntry)
      .pipe(
        finalize(() => { // end save
          context.patchState({ saving: false });
        })
      )
      .subscribe(
        (bookmark) => {
          // save thumbnails
          if (action.includeIndex) {
            const thumbnails = this.store.selectSnapshot(StoryChaptersState.getRegisteredThumbnails);
            context.dispatch(
              new SaveBookmarkThumbnails(
                method,
                bookmark.id,
                bookmarkEntry,
                thumbnails,
                invert(chapterIdMap)
              )
            );
          }

          const story: StoryModel = serialized ? serialized.story : null;
          

          // add/remove collections
          if (bookmarkEntry.addCollection || bookmarkEntry.removeCollection) {
            context.dispatch(new UpdateBookmarkCollection(bookmark, bookmarkEntry.addCollection, bookmarkEntry.removeCollection));

            this.actions.pipe(
              ofActionDispatched(UpdateBookmarkCollectionSuccess, UpdateBookmarkCollectionError),
              take(1)
            ).subscribe(() => {
              context.dispatch(new SaveBookmarkSuccess(bookmark, story, method));
            });
          } else {
            context.dispatch(new SaveBookmarkSuccess(bookmark, story, method));
          }

          
        },
        (err) => {
          context.dispatch(new SaveBookmarkFail(bookmarkEntry));
        });
  }

  @Action(SaveBookmarkSuccess)
  saveBookmarkSuccess(context: StateContext<BookmarksStateModel>, action: SaveBookmarkSuccess) {
    const savedBookmark = action.bookmark;
    const bookmarks = context.getState().bookmarks;

    // patch bookmark entry
    const savedBookmarkIndex = bookmarks.findIndex(bookmark => bookmark.id === savedBookmark.id);
    if (savedBookmarkIndex !== -1) {
      bookmarks[savedBookmarkIndex] = savedBookmark;
      context.patchState({
        bookmarks: [...bookmarks]
      });
    }

    // update selected bookmar if replacing (save)
    if (action.method === 'save') {
      context.patchState({
        selectedBookmark: action.bookmark
      });
    }
  }


  @Action(DeleteBookmark)
  deleteBookmark(context: StateContext<BookmarksStateModel>, action: DeleteBookmark) {
    const bookmarkId = action.bookmarkId;

    this.bookmarksService.deleteBookmark(bookmarkId)
      .subscribe(
        () => {
          context.dispatch(new DeleteBookmarkSuccess(bookmarkId));
        },
        (message) => {
          context.dispatch(new DeleteBookmarkFail(bookmarkId, message));
        });
  }

  @Action(DeleteBookmarkSuccess)
  deleteBookmarkSuccess(context: StateContext<BookmarksStateModel>, action: DeleteBookmarkSuccess) {
    const bookmarks = context.getState().bookmarks
      .filter(bookmark => bookmark.id !== action.bookmarkId);

    context.patchState({
      bookmarks: [...bookmarks]
    });

    this.snackbar.open('Successfully deleted 3D model');
  }

  @Action([DeleteBookmarkFail, DeleteFavoriteFail])
  deleteFail(context: StateContext<BookmarksStateModel>, action: DeleteBookmarkFail | DeleteFavoriteFail) {
    this.snackbar.open('There was an error deleting the 3D model');
  }

  @Action(DeleteFavorite)
  deleteFavorite(context: StateContext<BookmarksStateModel>, action: DeleteFavorite) {
    const bookmark = action.bookmark;
    const bookmarkId = bookmark.id;

    // category normalization: 'condition' | 'anatomy' | 'bookmark'
    const contentType = get(bookmark, 'category', 'bookmark');
    // id normalization
    const contentId = contentType === 'bookmark' ? bookmarkId : this.exploreService.toDatabaseContentId(bookmarkId);

    this.bookmarksService.deleteFavorite(contentId, contentType)
      .subscribe(
        () => {
          context.dispatch(new DeleteFavoriteSuccess(bookmarkId));
        },
        (err) => {
          context.dispatch(new DeleteFavoriteFail(bookmarkId));
        });
  }

  @Action(DeleteFavoriteSuccess)
  deleteFavoriteSuccess(context: StateContext<BookmarksStateModel>, action: DeleteFavoriteSuccess) {
    const bookmarks = context.getState().bookmarks
      .filter(bookmark => bookmark.id !== action.bookmarkId);

    context.patchState({
      bookmarks: [...bookmarks]
    });

    this.snackbar.open('Successfully deleted 3D model');
  }

  @Action(SortBookmarks)
  sortBookmarks(context: StateContext<BookmarksStateModel>, action: SortBookmarks) {
    const bookmarks = context.getState().bookmarks;

    context.patchState({
      sort: action.sort,
      bookmarks: [...bookmarks]
    });
  }

  @Action(ClearBookmarks)
  clearBookmarks(context: StateContext<BookmarksStateModel>, action: ClearBookmarks) {
    context.patchState({
      bookmarks: [],
      checkedBookmarks: []
    });
  }

  @Action(ResetBookmarks)
  resetBookmarks(context: StateContext<BookmarksStateModel>, action: ResetBookmarks) {
    context.setState(BookmarksStateDefaults);
  }

  @Action(CheckBookmark)
  checkBookmark(context: StateContext<BookmarksStateModel>, action: CheckBookmark) {
    let checkedBookmarks = context.getState().checkedBookmarks;
    const exists = checkedBookmarks.some(bookmark => bookmark === action.id);

    if (action.checked && !exists) {
      checkedBookmarks = [...checkedBookmarks, action.id];
    } else if (!action.checked) {
      checkedBookmarks = checkedBookmarks.filter(bookmark => bookmark !== action.id);
    }

    context.patchState({
      checkedBookmarks: checkedBookmarks
    });
  }

  @Action(SetCheckedBookmarks)
  setCheckedBookmarks(context: StateContext<BookmarksStateModel>, action: SetCheckedBookmarks) {
    context.patchState({
      checkedBookmarks: action.ids
    });
  }

  @Action(SaveBookmarkThumbnails)
  saveBookmarkThumbnails(context: StateContext<BookmarksStateModel>, action: SaveBookmarkThumbnails) {
    // chapter id mapping (new bookmark chapters have new ids)
    const chapterIdMap = action.chapterIdMap;

    // thumbnail urls on server are identified by story-id and chapter-id.
    const thumbnails = action.thumbnails;

    // replace/create thumbnails
    const requests$ = this.bookmarksService.saveBookmarkThumbnails(action.bookmarkId, action.bookmarkEntry, thumbnails, chapterIdMap);

    // conditionally delete thumbnails
    if (action.method === 'update') {
      // Delete extra thumbnails. We are saving over an existing bookmark
      const deleteRequests$ = this.bookmarksService.deleteBookmarkThumbnails(action.bookmarkId, action.bookmarkEntry, thumbnails, chapterIdMap);
      for (const deleteRequest$ of deleteRequests$) {
        requests$.push(deleteRequest$);
      }
    }

    forkJoin(requests$).subscribe((results) => {
      // On save success, the application will attempt to retrieve server thumbnails. 
      // The server has an unpredictable timeline for flushing the cache, so to predictably serve the user
      // the thumbnails they create, we instead serve the thumbnails from memory.
      context.dispatch(new RegisterChapterThumbnails(new Map()))
        .subscribe(() => {
          context.dispatch(new RegisterChapterThumbnails(action.thumbnails));
        });
    });
  }

  @Action(ExportBookmarks)
  exportBookmarks(context: StateContext<BookmarksStateModel>, action: ExportBookmarks) {   
    const { bookmarks, widgetURL, widgetOptions, offlineEnabled } = action;
    const exportWidgetOptions = widgetOptions || new WidgetOptions();

    context.patchState({ exporting: true });

    this.publishService.exportModels(bookmarks, widgetURL, exportWidgetOptions, null, action.userLanguage, action.userApp, offlineEnabled)
      .pipe(
        finalize(() => {
          context.patchState({ exporting: false });
        })
      )
      .subscribe((success) => {
        if (success) {
          context.dispatch(new ExportBookmarksSuccess(bookmarks));
        } else {
          context.dispatch(new ExportBookmarksError(bookmarks));
        }
      });
  }

  @Action(UpdateBookmarkCollection)
  updateBookmarkCollection(context: StateContext<BookmarksStateModel>, action: UpdateBookmarkCollection) {
    const { bookmark, addCollection, removeCollection } = action;
    const collectionContent: CollectionContentModel = {
      content_type: bookmark.type,
      content_id: bookmark.id
    };

    const operations$ = [];

    // important: removal need before adding, if applicable
    if (removeCollection) {
      operations$.push(
        // get bookmark's collection content id
        this.organizationsService.getCollectionContentId(removeCollection, collectionContent)
          .pipe(
            // validate id
            filter((collectionContentId) => !!collectionContentId),
            // remove bookmark via collection content id
            flatMap((collectionContentId) => this.organizationsService.removeFromCollection(removeCollection, [collectionContentId])),
            tap(
              () => context.dispatch(new RemoveBookmarkCollectionSuccess(bookmark, removeCollection)),
              () => context.dispatch(new RemoveBookmarkCollectionError(bookmark, removeCollection))
            )
          )
      );
    }

    // optionally: add to collection
    if (addCollection) {
      operations$.push(
        // add content to specified collection
        this.organizationsService.addToCollection(addCollection, [collectionContent])
          .pipe(
            tap(
              () => context.dispatch(new AddBookmarkCollectionSuccess(bookmark, removeCollection)),
              () => context.dispatch(new AddBookmarkCollectionError(bookmark, removeCollection))
            )
          )
      );
    }

    // execute operations
    concat(
      ...operations$
    )
      .subscribe(
        () => context.dispatch(new UpdateBookmarkCollectionSuccess(bookmark, addCollection, removeCollection)),
        () => context.dispatch(new UpdateBookmarkCollectionError(bookmark, addCollection, removeCollection))
      );
  }

  @Action(RemoveBookmarkCollectionSuccess)
  removeBookmarkCollectionSuccess(context: StateContext<BookmarksStateModel>, action: RemoveBookmarkCollectionSuccess) {
    const { bookmarks, selectedCategory } = context.getState();

    // if current state bookmarks are from collection data: update list
    if (selectedCategory === 'collection') {
      const bookmarkId = action.bookmark.id;
      const patchBookmarks = bookmarks
        .filter(bookmark => bookmark.id !== bookmarkId);

      context.patchState({
        bookmarks: [...patchBookmarks]
      });
    }
  }

  @Action(CopyBookmark)
  async copyBookmark(context: StateContext<BookmarksStateModel>, action: CopyBookmark) {
    const { groupAccess, collection } = action;
    let newBookmark: BookmarkModel;

    context.patchState({ copying: true, saving: true });

    // 1 --- Copy bookmark ---
    const id = await this.bookmarksService
      .copyBookmark(action.id, action.type, action.lang)
      .toPromise()
      .catch((err) => {
        context.dispatch(new CopyBookmarkFailure(action.id, err));
      });

    if (!id) {
      context.dispatch(new CopyBookmarkFailure(action.id, null));
      return;
    }

    // 2 --- Remap the particle system IDs ---
    const [preSaveJson, postSaveJson] = await Promise.all([
      this.storyService.getStory(action.id, action.type).toPromise(),
      this.storyService.getStory(id, ContentType.Bookmark).toPromise(),
    ]);

    // use the old and new json to map from old chapter Id -> new chapter Id
    const newChapterIdForOld = preSaveJson.chapters
      .map((chapter) => chapter.id)
      .reduce((acc, oldId, currentIndex) => {
        acc[oldId] = postSaveJson.chapters[currentIndex].id;
        return acc;
      }, {});

    // replace the old chapter Ids with the new ones, and patch the lookup.
    const lookup = mapKeys(
      postSaveJson.tweenParticleLookup,
      (value, oldChapterId) => newChapterIdForOld[oldChapterId]
    );
    postSaveJson.tweenParticleLookup = lookup;

    // 3 --- Save bookmark with updated configuration, including any changes ---
    const entry = new BookmarkEntryModel();
    entry.title = action.title;
    entry.isOwner = true;
    entry.id = id;
    entry.index = postSaveJson;

    if (groupAccess) {
      entry.add_group_access = [groupAccess];
    }

    newBookmark = await this.bookmarksService.saveBookmark(entry).toPromise();

    // 4 --- Optionally add boomark to collection ---
    if (groupAccess && collection) {
      const collectionConfig = [{ content_type: 'bookmark', content_id: id }];
      await this.organizationsService
        .addToCollection(collection, collectionConfig)
        .toPromise();
    }

    context.patchState({ copying: false, saving: false });
    context.dispatch(
      new CopyBookmarkSuccess(id, action.id, newBookmark)
    );
  }

  @Action(SetBookmarkText)
  setBookmarkText(context: StateContext<BookmarksStateModel>, action: SetBookmarkText) {
    const selectedBookmark = context.getState().selectedBookmark;

    context.patchState({
      selectedBookmark: {
        ...selectedBookmark,
        title: action.title
      }
    });
  }

  @Action(SetSelectedCategory)
  setSelectedCategory(context: StateContext<BookmarksStateModel>, { category }: SetSelectedCategory) {
    context.patchState({
      selectedCategory: category ? category : null
    });
  }

  @Action(CancelSaveBookmark)
  cancelSaveBookmark(context: StateContext<BookmarksStateModel>, action: CancelSaveBookmark) {
    // TODO: doesn't actually block saving
    context.patchState({
      saving: false
    });
  }

  @Action(CancelCopyBookmark)
  cancelCopyBookmark(context: StateContext<BookmarksStateModel>, action: CancelCopyBookmark) {
    // TODO: doesn't actually block copying
    context.patchState({
      copying: false
    });
  }

  constructor(
    private store: Store,
    private actions: Actions,
    private bookmarksService: BookmarksService,
    private storyService: StoryService,
    private exploreService: ExploreService,
    private publishService: PublishService,
    private organizationsService: OrganizationsService,
    private snackbar: MatSnackBar
  ) { }
}
